import { useEffect, useCallback, useRef, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { useProject } from '@/api/useProjects'
import { useUpdatePage } from '@/api/usePages'
import { useEditorStore } from '@/stores/useEditorStore'
import { useProjectStore } from '@/stores/useProjectStore'
import { EditorToolbar } from '@/components/editor/EditorToolbar/EditorToolbar'
import { EditorSidebar } from '@/components/editor/EditorSidebar/EditorSidebar'
import { EditorCanvas } from '@/components/editor/EditorCanvas/EditorCanvas'
import { PropertyPanel } from '@/components/editor/PropertyPanel/PropertyPanel'
import { PageStrip } from '@/components/editor/PageStrip/PageStrip'
import { ZoomControls } from '@/components/editor/ZoomControls/ZoomControls'
import { Spinner } from '@/components/ui'
import { ROUTES } from '@/constants/routes'
import styles from './Editor.module.css'

export function Editor() {
    const { projectId } = useParams<{ projectId: string }>()
    const navigate = useNavigate()
    const id = projectId || ''
    const { data: project, isLoading, error } = useProject(id)
    const { setProject, resetProject } = useProjectStore()
    const { resetEditor, isDirty, setIsDirty, setIsSaving } = useEditorStore()
    const queryClient = useQueryClient()
    const updatePage = useUpdatePage()
    const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const [canvasInstance, setCanvasInstance] = useState<unknown>(null)

    useEffect(() => {
        if (project) {
            setProject({
                id: project.id,
                name: project.name,
                document_type_name: project.document_type_name,
                width_mm: project.width_mm,
                height_mm: project.height_mm,
                pages: project.pages,
                status: project.status,
            })
        }
    }, [project, setProject])

    useEffect(() => {
        return () => {
            if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
            resetEditor()
            resetProject()
            queryClient.invalidateQueries({ queryKey: ['projects'] })
        }
    }, [resetEditor, resetProject, queryClient])

    const doSave = useCallback(() => {
        const { pages } = useProjectStore.getState()
        const { currentPageIndex, isDirty: dirty, isSaving } = useEditorStore.getState()
        const page = pages[currentPageIndex]

        if (!page || !dirty || isSaving) return

        setIsSaving(true)
        updatePage.mutate(
            { projectId: id, pageId: page.id, canvas_json: page.canvas_json },
            {
                onSuccess: () => {
                    setIsDirty(false)
                    setIsSaving(false)
                },
                onError: () => {
                    toast.error('Error al guardar')
                    setIsSaving(false)
                },
            }
        )
    }, [id, setIsDirty, setIsSaving, updatePage])

    const handleSave = useCallback(() => {
        if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
        doSave()
    }, [doSave])

    useEffect(() => {
        if (!isDirty) return

        if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
        saveTimerRef.current = setTimeout(() => {
            doSave()
        }, 3000)

        return () => {
            if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
        }
    }, [isDirty, doSave])

    if (isLoading) {
        return (
            <div className={styles.loading}>
                <Spinner size="lg" />
            </div>
        )
    }

    if (error || !project) {
        return (
            <div className={styles.error}>
                <p>No se pudo cargar el proyecto</p>
                <button onClick={() => navigate(ROUTES.projects)}>Volver a proyectos</button>
            </div>
        )
    }

    return (
        <div className={styles.editor}>
            <EditorToolbar projectName={project.name} projectId={id} onSave={handleSave} />
            <div className={styles.workspace}>
                <EditorSidebar />
                <div className={styles.canvasArea}>
                    <div className={styles.canvasWrapper}>
                        <EditorCanvas onCanvasReady={setCanvasInstance} />
                        <ZoomControls />
                    </div>
                    <PageStrip />
                </div>
                <PropertyPanel canvasInstance={canvasInstance} />
            </div>
        </div>
    )
}
